'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import CookieConsent from './CookieConsent';
import CookieSettings from './CookieSettings';
import { getCookiePreferences, CookiePreferences } from '@/lib/cookies';
import { handleConsentChange, initializeTracking } from '@/lib/scriptLoader';

// Configuration for tracking scripts
const TRACKING_CONFIG = {
  googleTagManager: 'GTM-PKBP3VNS',
  hotjarId: '6538712',
  clarityId: 'tngi0oncrw',
};

// Routes where CookieManager should be hidden
const HIDDEN_ROUTES = ['/parthik', '/chirag'];

export default function CookieManager() {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [showInitialConsent, setShowInitialConsent] = useState(false);

  // Normalize pathname by removing trailing slash
  const normalizedPath = pathname?.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;

  // Check if current route should hide the cookie manager
  const shouldHide = HIDDEN_ROUTES.includes(normalizedPath);

  useEffect(() => {
    setMounted(true);

    // Don't initialize tracking on hidden routes
    if (shouldHide) return;

    // Check if user has already given consent
    const existingPreferences = getCookiePreferences();

    if (!existingPreferences) {
      // First time visit - show consent modal
      setShowInitialConsent(true);
    } else {
      // Returning visit - initialize tracking with stored preferences
      initializeTracking(TRACKING_CONFIG);
    }
  }, [shouldHide]);

  const handlePreferencesChange = (preferences: CookiePreferences) => {
    setShowInitialConsent(false);

    // Handle consent change - reinitialize tracking scripts
    handleConsentChange(preferences, TRACKING_CONFIG);
  };

  // Don't render on server-side to avoid hydration issues
  if (!mounted) return null;

  // Don't render on hidden routes
  if (shouldHide) return null;

  return (
    <>
      {/* Initial Consent Modal */}
      {showInitialConsent && (
        <CookieConsent onPreferencesChange={handlePreferencesChange} />
      )}

      {/* Cookie Settings Button */}
      <CookieSettings onPreferencesChange={handlePreferencesChange} />
    </>
  );
}
